import { useEffect, useMemo, useState } from "react";
import { BookingInfoSlotDefinitionDto } from "../../../graphql/models/Booking";
import {
  Button,
  Checkbox,
  Grid,
  IconButton,
  TextField,
} from "@material-ui/core";
import { useDateTimeUtils } from "../../../hooks";
import { CheckCircleOutline, DeleteOutline } from "@material-ui/icons";
import { textFieldClasses } from "@mui/material";
import { Alert } from "@material-ui/lab";

export interface BookingRangeProps {
  value: BookingInfoSlotDefinitionDto | undefined;
  disabled?: boolean;
  onChange: (value: BookingInfoSlotDefinitionDto) => void;
  onDelete: () => void;
}

export interface BookingRangeCollectionProps {
  value: BookingInfoSlotDefinitionDto[];
  disabled?: boolean;
  onChange: (value: BookingInfoSlotDefinitionDto[]) => void;
}

export function BookingRange(props: BookingRangeProps) {
  const { value, disabled } = props;
  const [start, setStart] = useState(value?.from ?? "");
  const [end, setEnd] = useState(value?.to ?? "");
  const [advance, setAdvance] = useState(value?.advanceInMinutes);
  const [overrideAdvance, setOverrideAdvance] = useState(advance !== undefined);
  const [hasChanges, setHasChanges] = useState(false);

  const { checkRange } = useDateTimeUtils();
  const handleConferma = () => {
    const check = checkRange(start, end);
    if (!check) alert("Verifica i valori inseriti (formato e ordine)");
    else {
      props.onChange({
        from: start,
        to: end,
        advanceInMinutes: advance,
      });
      setHasChanges(false);
    }
  };

  const handleAdvanceChange = (value: string) => {
    const v = parseInt(value);
    setAdvance(v);
  };

  const showAdvanceAlert = useMemo(() => {
    if (!overrideAdvance) return false;
    return advance === undefined || isNaN(advance) || advance < 0;
  }, [advance, overrideAdvance]);

  const handleDelete = () => {
    props.onDelete();
  };

  useEffect(() => {
    if (
      start === value?.from &&
      end === value?.to &&
      advance === value?.advanceInMinutes
    ) {
      setHasChanges(false);
    } else {
      setHasChanges(true);
    }
  }, [start, end, advance]);

  return (
    <Grid container spacing={1}>
      <Grid item lg={12}>
        <hr></hr>
        <Checkbox
          checked={overrideAdvance}
          onChange={(e, checked) => {
            setOverrideAdvance(checked);
          }}
        />{" "}
        Imposta anticipo personalizzato
      </Grid>
      {overrideAdvance && (
        <Grid item lg={12}>
          <TextField
            type="number"
            label="Anticipo (minuti)"
            variant="outlined"
            value={advance}
            onChange={(e) => {
              handleAdvanceChange(e.target.value);
            }}
            style={{ width: "100%" }}
          ></TextField>
          {showAdvanceAlert && (
            <Alert severity="error">Valore non valido</Alert>
          )}
        </Grid>
      )}
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          type="text"
          classes={textFieldClasses}
          disabled={disabled}
          variant="outlined"
          label="Dalle ore"
          value={start}
          onChange={(e) => setStart(e.target.value)}
        ></TextField>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          type="text"
          classes={textFieldClasses}
          disabled={disabled}
          variant="outlined"
          label="alle ore"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
        ></TextField>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        {hasChanges && (
          <IconButton
            aria-label="Conferma"
            size="medium"
            onClick={handleConferma}
            disabled={disabled && !hasChanges}
            style={{ marginTop: "4px" }}
          >
            <CheckCircleOutline fontSize={"inherit"} />
          </IconButton>
        )}
        <IconButton
          aria-label="Elimina"
          size="medium"
          onClick={handleDelete}
          disabled={disabled}
          style={{ marginTop: "4px" }}
        >
          <DeleteOutline fontSize={"inherit"} />
        </IconButton>
      </Grid>
    </Grid>
  );
}

export function BookingRangeCollection(props: BookingRangeCollectionProps) {
  const { value, disabled } = props;
  const [items, setItems] = useState(value);

  const handleChange = (
    newItem: BookingInfoSlotDefinitionDto,
    index: number
  ) => {
    const newCollection = [...items];
    newCollection.splice(index, 1, newItem);
    setItems(newCollection);
    props.onChange(newCollection);
  };

  const handleNewItem = () => {
    const newItem: BookingInfoSlotDefinitionDto = {
      from: "",
      to: "",
    };
    const newCollection = [...items, newItem];
    setItems(newCollection);
    props.onChange(newCollection);
  };

  const handleDelete = (index: number) => {
    const newCollection = [...items];
    newCollection.splice(index, 1);
    setItems(newCollection);
    props.onChange(newCollection);
  };

  return (
    <div>
      {value.map((item, index) => {
        return (
          <BookingRange
            key={`key${index}`}
            disabled={disabled}
            value={item}
            onChange={(newItem) => handleChange(newItem, index)}
            onDelete={() => handleDelete(index)}
          />
        );
      })}
      {value.length === 0 && (
        <Alert severity="info">
          non sono stati definiti ranges orari per questo giorno
        </Alert>
      )}
      <Button
        type="button"
        disabled={disabled}
        onClick={handleNewItem}
        variant={"outlined"}
      >
        (+) Aggiungi un range
      </Button>
    </div>
  );
}
